import { Inject, Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import {
  BillService,
  License,
  Card,
  LicenseAndCard
} from './bill.service';
import {
  MessageService, MessageUserUpdated, MessageLicenseAndCardUpdated, MessageSWLicenseUpdated,
  MessagePromptOpened,
  MessageLogout,
  MessageCheckOut,
  MessageLoginOnClick,
  MessageShowUpgrade,
  MessageUserServiceInitialized,
  MessageShowAppInstallPopup,
  MessageUserMetadataUpdated,
  MessageCheckNeedForComm
} from './message.service';
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig } from '@angular/material/legacy-dialog';
import {
  upgradePromptMsg, convertWithPlusVoiceNeedUpgradeMsg, convertWithPremVoiceNeedUpgradeMsg,
  whatIsOCRMsg, needOcrText, needOcrTextSW, voiceCantBeConvertedMsg, convertWithFreeVoiceNeedUpgradeMsg,
} from './constant.service';
import { AppService } from './app.service';
import { HttpClient } from '@angular/common/http';
import { LayoutService } from 'src/app/services/layout.service';
import { SettingsService } from './settings.service';
// import { PaymentComponent } from 'src/app/modules-2022/lazy-loaded/pw-payment/components/payment/payment.component';
import { LazyLoadingService } from './lazy-loading.service';
import { PwPaymentComponent } from '../ui-test/pw-payment/pw-payment.component';
import { PwAlertDialogComponent } from '../ui-test/pw-alert-dialog/pw-alert-dialog.component';
import { DialogUpgradeComponent } from '../modules-2022/lazy-loaded/app-initial/components/dialog-upgrade/dialog-upgrade.component';
import { AmplifyControllerService } from './amplify-controller.service';
import { AppInstallService } from './app-install.service';
export class UserInfo {
  constructor(
    public cognitoId: string,
    public licNum: string,
    public licExpiry: string,
    public autoRenew: boolean,
    public isExpired = false,
  ) { }
}

export enum PWRole {
  free = 'free',
  premium = 'premium',
  plus = 'plus'
}

// TODO: edu
export enum EDURole {
  premium = 'premium',
  plus = 'plus'
}

export enum EDULicense {
  premiumUserLicense = 'Free EDU Premium Group License'
}

export enum SWRole {
  free = 'free',
  personal = 'personal',
  pro = 'pro',
  ultimate = 'ultimate'
}

export enum SWLicense {
  freeUserLicense = 'Free User License',
  personalUserLicense = 'Personal User License',
  proUserLicense = 'Professional User License',
  ultimateUserLicense = 'Ultimate User License'
}

export enum DefaultUser {
  id = '0',
  username = ''
}

export interface User {
  id: string;
  email: string;
  nickname: string;
  license: any;
  username: string;
}

export class PWUser implements User {
  constructor(
    public id: string | DefaultUser = DefaultUser.id,
    public username: string | DefaultUser = DefaultUser.username,
    public email: string = 'user@naturalreaders.com',
    public nickname: string = 'nickname',
    public license: License = new License(),
    public eduLicense: License = null,
    public card: Card = null,
    public metadata: Object = {}
  ) { }
}

export class SWUser implements User {
  constructor(
    public id: string | DefaultUser = DefaultUser.id,
    public username: string | DefaultUser = DefaultUser.username,
    public email: string = 'user@naturalreaders.com',
    public nickname: string = 'nickname',
    public license: SWLicense = SWLicense.freeUserLicense
  ) { }
}

// NAPersonal-userinfo - set in SSO
export class LoginUserInfo {
  constructor(
    public rst: string,
    public userid: string, // email
    public name: string,
    public id: string,
    public username: string,
    public isactive: string,
  ) { }
}

let userServiceFactory = (
  messageService: MessageService,
  storageService: StorageService,
  settingsService: SettingsService,
  layout: LayoutService,
  lazyLoadingService: LazyLoadingService,
  billService: BillService,
  amplifyControllerService: AmplifyControllerService,
  appInstallService: AppInstallService,
  //   sharedUtilService: SharedUtilService,
  dialog: MatDialog,
  http: HttpClient) => {
  if (AppService.isPW() || AppService.isAndroid() || AppService.isIOS()) {
    console.log("user factory: is pw");
    return new PWUserService(storageService, messageService, settingsService, layout, lazyLoadingService, billService, dialog, amplifyControllerService, appInstallService);
  } else {
    console.log("user factory: is sw");
    // TODO
    // return new SWUserService(http, storageService, messageService, settingsService, billService, sharedUtilService, dialog);
    return new SWUserService(http, storageService, messageService, settingsService, layout, lazyLoadingService, dialog);
  }
};

@Injectable({
  providedIn: 'root'
})
export abstract class UserService {

  protected user: User;

  /** Edu Expired TEST **/
  isEDUExpired: boolean = true;
  /** Edu Expired TEST **/

  constructor(
    protected storageService: StorageService,
    protected messageService: MessageService,
    protected settingsService: SettingsService,
    protected layout: LayoutService,
    protected lazyLoadingService: LazyLoadingService) {
    this.messageService.getMessage().subscribe(message => {
      if (message.name === MessageCheckOut) {
        this.checkout(message.data);
      } else if (message.name === MessageShowUpgrade) {
        this.showUpgrade(message.data);
      }
    });
    this.messageService.sendMessage({ name: MessageUserServiceInitialized });
  }


  public abstract setUser(userInfo: User): Promise<User>;

  public getUser(): User {
    return this.user;
  }

  public getLicense(): License | SWLicense {
    return this.user ? this.user.license : null;
  }

  public getUserId(): string {
    return this.user ? this.user.id : DefaultUser.id;
  }

  public getUsername(): string {
    return this.user ? this.user.username : DefaultUser.username;
  }

  public getUserInfoFromLocalStorage(): LoginUserInfo {
    const loginUserInfo: LoginUserInfo = this.storageService.read<LoginUserInfo>('NAPersonal-userinfo');
    return loginUserInfo;
  }

  public getUserInfoJsonStrFromLocalStorage(): string {
    const loginUserInfo: string = this.storageService.readWithoutJsonParse('NAPersonal-userinfo');
    return loginUserInfo;
  }

  public getLicenseNum() {
    // TODO: think about how to handle
    return 0;
  }

  public getEduLicenseOwnerEmail() {
    return '';
  }

  public abstract getUserRole(): PWRole | SWRole | EDURole;

  public abstract clearUser();

  public abstract showUpgrade(type: string);

  public abstract getOrderPrompt(plan);

  public abstract showOrderPrompt(feature: string);

  public abstract checkout(plan);

  public abstract upgrade(type: string);

  public abstract upgradeSubscription(isUpgrade: boolean);
}

export class PWUserService extends UserService {
  constructor(
    @Inject(StorageService) storageService: StorageService,
    @Inject(MessageService) messageService: MessageService,
    @Inject(SettingsService) settingsService: SettingsService,
    @Inject(LayoutService) layout: LayoutService,
    @Inject(LazyLoadingService) lazyLoadingService: LazyLoadingService,
    private billService: BillService, public dialog: MatDialog,
    private amplifyControllerService: AmplifyControllerService,
    private appInstallService: AppInstallService) {
    super(storageService, messageService, settingsService, layout, lazyLoadingService);
    this.user = new PWUser();
  }

  public setUser(userInfo: User): Promise<PWUser> {
    console.log("setUser");
    if (!userInfo) {
      this.messageService.sendMessage({ name: MessageUserUpdated, data: this.user });
      return Promise.resolve(this.user as PWUser);
    }

    const id = userInfo.id;
    const username = userInfo.username;
    const email = userInfo.email; // userid is email

    const nickname = userInfo.nickname;
    this.user = new PWUser(id, username, email, nickname);
    const originalLicense = this.getLicenseFromLocalStorage();

    if (originalLicense) {
      this.user.license = originalLicense;
    }

    // TODO: edu originalEduLicense
    const originalEduLicense = this.getEduLicenseFromLocalStorage();
    if (originalEduLicense) {
      (this.user as PWUser).eduLicense = originalEduLicense;
    }

    this.messageService.sendMessage({ name: MessageUserUpdated, data: this.user });
    return this.getAndProcessLicenseAndCard(id, username)
      .then(async _ => {
        console.log('setUser user:', this.user);
        return Promise.resolve(this.user as PWUser);
      });
  }

  public setUserMetadata(type = '') {
    return this.amplifyControllerService.getSession()
      .then(userSession => {
        const jwtToken = userSession.getIdToken().getJwtToken();
        const config = {
          response: false,
          headers: {
            'Authorization': jwtToken,
            'Content-Type': 'application/json'
          },
          body: {
            userId: this.getUserId(),
            licNum: this.getLicenseNum()
          }
        };
        if (!type) {
          config.body['ttsUsage'] = true;
          config.body['mobileApps'] = true;
        } else {
          if (type === 'mobileApps') {
            config.body['mobileApps'] = true;
          }
          if (type === 'ttsUsage') {
            config.body['ttsUsage'] = true;
          }
        }
        return this.amplifyControllerService.getAPI()
          .then(API => {
            return API.post('ExtendedApi', '/user-metadata', config)
          })
          .then(res => {
            console.log('userMetadata response ', res);
            if (type === 'mobileApps') {
              if (res['android'] || res['ios']) {
                this.appInstallService.setMobileInstalled(true);
              } else {
                this.appInstallService.setMobileInstalled(false);
              }
            }

            if ('charsUsedPremTtsFree' in res && !this.isPaid()) {
              (<PWUser>this.user).metadata['charsUsedPremTtsFree'] = res.charsUsedPremTtsFree;
            }
            if ('charsUsedPlusTtsFree' in res) {
              (<PWUser>this.user).metadata['charsUsedPlusTtsFree'] = res.charsUsedPlusTtsFree;
            }
            if ('charsUsedPremTtsPaid' in res) {
              (<PWUser>this.user).metadata['charsUsedPremTtsPaid'] = res.charsUsedPremTtsPaid;
            } else {
              if (this.isPaid()) {
                (<PWUser>this.user).metadata['charsUsedPremTtsPaid'] = 'unlimited';
              }
            }
            if ('charsUsedPlusTtsPaid' in res) {
              (<PWUser>this.user).metadata['charsUsedPlusTtsPaid'] = res.charsUsedPlusTtsPaid;
            }
            console.log("usermetadata", (<PWUser>this.user).metadata);
            return Promise.resolve();
          })
          .then(() => {
            this.messageService.sendMessage({ name: MessageUserMetadataUpdated });
          })
          .catch(err => {
            this.messageService.sendMessage({ name: MessageUserMetadataUpdated });
            console.log("err in err in setUserMetadata", err);
            return Promise.resolve();
          });
      })
      .catch(err => {
        console.log("err in setUserMetadata", err);
        this.messageService.sendMessage({ name: MessageUserMetadataUpdated });
        return Promise.resolve();
      });
  }

  public isPaid() {
    const licNum = this.getLicenseNum();
    const licenceNumRank = {
      free: [0, 31, 38, 39], //38, 39 deprecated
      prem: [12, 32, 33, 34, 35, 36, 37, 40, 41],
      plus: [13, 42]
    };
    console.log("user is paid", licenceNumRank.free.includes(licNum))
    return !licenceNumRank.free.includes(licNum);
  }

  public getAndProcessLicenseAndCard(id = '', username = ''): Promise<any> {
    if (username.length === 0) {
      username = this.getUsername();
    }
    if (id.length === 0) {
      id = this.getUserId();
    }
    if (id === DefaultUser.id) {
      return Promise.resolve(null);
    }

    return this.billService.getLicenseAndCard(id, username)
      .then(licenseAndCard => {
        // this.user = new User(id, email, nickname, license)
        console.log('getLicenseAndCard: ', licenseAndCard);
        this.storageService.write('pw-license', licenseAndCard.license);
        this.user.license = licenseAndCard.license;
        (this.user as PWUser).card = licenseAndCard.card;
        // TODO: edu
        this.storageService.write('edu-license', licenseAndCard.eduLicense);
        (this.user as PWUser).eduLicense = licenseAndCard.eduLicense;
        // not display prem subscription for prem user
        // if (this.user.license.planName === PlanName.freePlan || this.user.license.isExpired) {
        //   localStorage.setItem('NaCordovaPremPaid', 'no');
        //   localStorage.setItem('NaCordovaPlusPaid', 'no');
        // } else if (this.user.license.planName === PlanName.premPlan) {
        //   localStorage.setItem('NaCordovaPremPaid', 'yes');
        //   localStorage.setItem('NaCordovaPlusPaid', 'no');
        // } else if (this.user.license.planName === PlanName.plusPlan) {
        //   localStorage.setItem('NaCordovaPremPaid', 'yes');
        //   localStorage.setItem('NaCordovaPlusPaid', 'yes');
        // }
      })
      .then(() => {
        return this.setUserMetadata('mobileApps');
      })
      .then(() => {
        console.log("send out MessageLicenseAndCardUpdated");
        this.messageService.sendMessage({ name: MessageLicenseAndCardUpdated, data: this.getLicenseAndCard() });
        this.messageService.sendMessage({ name: MessageCheckNeedForComm, data: {license: this.getLicenseNum() } });
      });
  }

  public getLicenseAndCard(): LicenseAndCard {
    // TODO: edu
    return this.user ? { license: this.user.license, eduLicense: (this.user as PWUser).eduLicense, card: (this.user as PWUser).card } : null;
  }

  public getLicenseFromLocalStorage(): License {
    const license: License = this.storageService.read('pw-license');

    return license;
  }

  public getEduLicenseFromLocalStorage(): License {
    const license: License = this.storageService.read('edu-license');

    return license;
  }

  public getLicenseJsonStrFromLocalStorage(): any {
    // TODO: edu ext
    let licenseNum = this.getLicenseNum();
    let pwLicense = this.storageService.readWithoutJsonParse('pw-license');
    if (licenseNum >= 30) {
      return { license: this.storageService.readWithoutJsonParse('edu-license'), pwLicense: pwLicense };
    } else {
      return { license: pwLicense, pwLicense: pwLicense };
    }
  }

  public override getEduLicenseOwnerEmail(): string {
    let eduLicense = (this.user as PWUser).eduLicense && !(this.user as PWUser).eduLicense.isExpired ? (this.user as PWUser).eduLicense : null;

    if (eduLicense) {
      return eduLicense.ownerEmail;
    }

    return '';
  }

  public override getLicenseNum(): number {
    // TODO: edu
    // const licenceNumRank = {
    // free: [0, 31, 38, 39], //38, 39 deprecated
    // prem: [12, 32, 33, 34, 35, 36, 37, 40, 41],
    // plus: [13, 42]
    // };
    const pwLicenseNum = this.user.license && !this.user.license.isExpired ? this.user.license.num : 0;
    const eduLicenseNum = (this.user as PWUser).eduLicense && !(this.user as PWUser).eduLicense.isExpired ?
      (this.user as PWUser).eduLicense.num : 0;
    // if (this.user && this.user.license && !this.user.license.isExpired) {
    //   return this.user.license.num;
    // } else {
    //   return 0;
    // }
    if (pwLicenseNum === 13) {
      return pwLicenseNum;
    }
    else if (pwLicenseNum === 12) {
      return pwLicenseNum;
    } else {
      if (eduLicenseNum >= 32) {
        return eduLicenseNum;
      } else if (eduLicenseNum === 31) {
        return 31;
      } else {
        return 0;
      }
    }
  }

  public getUserIdFromLocalStorage(): string {
    const loginUserInfo: LoginUserInfo = this.storageService.read('NAPersonal-userinfo');

    return loginUserInfo ? loginUserInfo.id : '0';
  }

  public getUserRole(): PWRole | EDURole {
    // TODO: edu
    const pwLicenseNum = this.user && this.user.license && !this.user.license.isExpired ? this.user.license.num : 0;
    const eduLicense = this.user && (this.user as PWUser).eduLicense;
    let eduLicenseNum = 0;
    if (eduLicense && !eduLicense.isExpired && eduLicense.num) {
      eduLicenseNum = eduLicense.num;
    }

    console.log('pw edu lic num', pwLicenseNum, eduLicenseNum);

    if (pwLicenseNum === 12) {
      return PWRole.premium;
    } else if (pwLicenseNum === 13) {
      return PWRole.plus;
    } else {
      if (eduLicenseNum == 42) {
        return EDURole.plus;
      }
      else if (eduLicenseNum == 38 || eduLicenseNum == 39) { // these edu licenseNum are deprecated.
        return PWRole.free;
      }
      else if (eduLicenseNum >= 32 && eduLicenseNum <= 41) {
        return EDURole.premium;
      }
      else {
        return PWRole.free;
      }
    }
  }

  clearUser() {
    return new Promise((resolve) => {
      this.user = new PWUser();
      this.storageService.write('pw-license', this.user.license);
      // TODO: edu
      this.storageService.write('edu-license', (this.user as PWUser).eduLicense);
      resolve(null);
    })
      .then(() => {
        this.messageService.sendMessage({ name: MessageUserUpdated, data: this.user });
        this.messageService.sendMessage({ name: MessageLicenseAndCardUpdated, data: this.getLicenseAndCard() });
      });
  }

  showUpgrade(type: string) {
    // TODO: edu
    const pwLicenseNum = this.user && this.user.license && !this.user.license.isExpired ? this.user.license.num : 0;
    const eduLicense = this.user && (this.user as PWUser).eduLicense;
    let isEdu = false;
    // let eduLicenseNum = 0;
    if (eduLicense) {
      console.log('eduUpgrade here');
      this.isEDUExpired = eduLicense.isExpired;
      // eduLicenseNum = eduLicense.num;
      console.log('eduUpgrade: ', this.isEDUExpired, eduLicense.num);
      isEdu = true;
    }
    // console.log('edu checkout: ', eduLicenseNum);
    // if (eduLicenseNum === 31) {
    //   this.isEDUExpired = true;
    // }
    switch (pwLicenseNum) {
      case 12:
        this.upgradeSubscription();
        break;
      default:
        if (isEdu && this.isEDUExpired && !eduLicense.isOwner && type != 'plus') {
          this.askForEDU(type);
        } else {
          this.checkout(type);
        }

      //   if (isEdu && this.isEDUExpired) {
      //     if (!eduLicense.isOwner) {
      //       this.askForEDU(type);
      //     } else {
      //       this.checkout('edu');
      //     }
      //   } else {
      //     if (isEdu) {
      //       this.checkout('plus');
      //     } else {
      //       this.checkout(type);
      //     }
      //   }
      // // this.checkout(type);
    }
  }

  /** Edu test **/
  askForEDU(type: string) {
    const eduLicense = this.user && (this.user as PWUser).eduLicense;
    const eduOwnerEmail = eduLicense.ownerEmail;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.autoFocus = false;
    dialogConfig.width = '375px';
    dialogConfig.data = {
      type: 'edu',
      // type: type == 'plus' ? 'edu-plus' : 'edu',
      title: "",
      htmlTitle: null,
      titleTooltip: null,
      text: "",
      data: {},
      ownerEmail: eduOwnerEmail,
      button_left: null,
      button_center: null,
      button_right: null,
    };
    // TODO
    const dialogRef = this.dialog.open(PwAlertDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      //   // if (result === "Education") {
      //   //   const eduLicense = this.user && (this.user as PWUser).eduLicense;
      //   //   const eduOwnerEmail = eduLicense.ownerEmail;
      //   //   if (eduLicense.isOwner) {
      //   //     this.checkout('edu');
      //   //   } else {
      //   //     const dialogConfig = new MatDialogConfig();
      //   //     dialogConfig.autoFocus = false;
      //   //     dialogConfig.width = '375px';
      //   //     dialogConfig.data = {
      //   //       type: null,
      //   //       title: null,
      //   //       htmlTitle: null,
      //   //       titleTooltip: null,
      //   //       text: 'Please contact the edu license owner to upgrade',
      //   //       data: {},
      //   //       button_left: null,
      //   //       button_center: 'ok',
      //   //       button_right: null,
      //   //     };
      //   //     const dialogRef = this.dialog.open(PwAlertDialogComponent, dialogConfig);
      //   //   }
      //   // } else if (result === 'Personal') {
      //   //   this.checkout(type);
      //   // }
    });
  }
  /** Edu test **/

  public upgradeYearly() {
    this.lazyLoadingService.loadAppInitialModule()
      .then(_ => {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.disableClose = true;
        dialogConfig.id = 'upgrade-subscription-dialog';
        let isMonthly = true;
        if (this.user.license.licCode === '6006' || this.user.license.licCode === '6008') {
          isMonthly = false;
        }

        // TODO
        dialogConfig.data = {
          isAutoRenew: this.user.license.isAutoRenew,
          isMonthly,
          liccode: this.user.license.licCode,
          email: this.user.email,
          userPlan: this.getUser().license.planName,
        };

        let _openComponent;
        // if (AppService.isAndroid() || AppService.isIOS()){

        // dialogConfig.panelClass = 'pw-payment-panel';
        if (AppService.isAndroid() || AppService.isIOS()) {
          dialogConfig.width = '100%';
          dialogConfig.height = '100%';
          // dialogConfig.panelClass = ['pw-payment-dialog', 'pw-full-handset'];
        } else {
          dialogConfig.width = '420px';
        }
        // dialogConfig.autoFocus = false;
        // _openComponent = DialogUpgradeComponent;
          dialogConfig.data.type = 'upgrade plus',
          dialogConfig.width = '420px';
          dialogConfig.panelClass = "pw-full-handset";
          dialogConfig.panelClass = 'pw-alert-dialog';
          dialogConfig.backdropClass = 'pw-dialog-backdrop';
          _openComponent = PwAlertDialogComponent;

        // TODO
        const dialogRef = this.dialog.open(_openComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(_ => {
          this.getAndProcessLicenseAndCard(this.getUserId());
        });
      });

  }

  public upgradeSubscription(isUpgrade = true) {
    this.lazyLoadingService.loadAppInitialModule()
      .then(_ => {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.disableClose = true;
        dialogConfig.id = 'upgrade-subscription-dialog';
        let isMonthly = true;
        if (this.user.license.licCode === '6006' || this.user.license.licCode === '6008') {
          isMonthly = false;
        }

        // TODO
        dialogConfig.data = {
          isAutoRenew: this.user.license.isAutoRenew,
          isMonthly,
          liccode: this.user.license.licCode,
          email: this.user.email,
          userPlan: this.getUser().license.planName,
          isUpgrade
        };

        let _openComponent;
        // if (AppService.isAndroid() || AppService.isIOS()){

        // dialogConfig.panelClass = 'pw-payment-panel';
        if (AppService.isAndroid() || AppService.isIOS()) {
          dialogConfig.width = '100%';
          dialogConfig.height = '100%';
          dialogConfig.panelClass = ['pw-payment-dialog', 'pw-full-handset'];
        } else {
          dialogConfig.width = '420px';
          dialogConfig.panelClass = 'pw-upgrade-dialog';
        }
        dialogConfig.autoFocus = false;
        _openComponent = DialogUpgradeComponent;
        // } else {
        //   dialogConfig.data.type = 'upgrade plus',
        //   dialogConfig.width = '420px';
        //   dialogConfig.panelClass = "pw-full-handset";
        //   dialogConfig.panelClass = 'pw-alert-dialog';
        //   dialogConfig.backdropClass = 'pw-dialog-backdrop';
        //   _openComponent = PwAlertDialogComponent;
        // }

        // TODO
        const dialogRef = this.dialog.open(_openComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(_ => {
          this.getAndProcessLicenseAndCard(this.getUserId());
        });
      });
  }

  public getOrderPrompt(feature) {
    switch (feature) {
      case 'convert':
        let voiceType = this.settingsService.getSelectedVoiceType();
        if (voiceType === 'free') {
          return { msg: convertWithFreeVoiceNeedUpgradeMsg, plan: 'prem', isHtml: true }
        } if (voiceType === 'prem') {
          if (this.settingsService.getSelectedPremVoice().source === 'google') {
            return { msg: voiceCantBeConvertedMsg, btnText: 'Okay', isHtml: true };
          } else {
            return { msg: convertWithPremVoiceNeedUpgradeMsg, plan: 'prem' }
          }
        } else {
          return { msg: convertWithPlusVoiceNeedUpgradeMsg, plan: 'plus' }
        }
      case 'ocr':
        return { msg: needOcrText, plan: 'plus', isHtml: true };
      default:
        return { msg: upgradePromptMsg, plan: 'plus' };
    }
  }

  public showOrderPrompt(feature: string) {
    console.log("showing order prompt");
    let buttonText = 'Order Now'

    // general
    let prompt = this.getOrderPrompt(feature);
    // let promptMsg = prompt.msg ? prompt.msg : '';
    buttonText = prompt.btnText ? prompt.btnText : buttonText;

    this.messageService.sendMessage({ name: MessagePromptOpened });
    // TODO
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'pw-alert-dialog';
    dialogConfig.backdropClass = 'pw-dialog-backdrop';
    dialogConfig.autoFocus = false;
    if (feature == 'convert') {
      let voiceType = this.settingsService.getSelectedVoiceType();
      if (voiceType === 'plus') {
        let orderButtonDisabled = false;
        let unsupportedMsg = '';
        if (this.user.license.type !== AppService.appName) {
          orderButtonDisabled = true;
          let platformName = '';
          switch (this.user.license.type) {
            case 'ios':
              platformName = 'iOS';
              break;
            case 'android':
              platformName = 'Android';
              break;
            default:
              platformName = 'Web/Desktop';
          }
          unsupportedMsg = 'Please order through the ' + platformName + ' app.';
        }
        dialogConfig.data = {
          type: "order plus",
          orderDisabled: orderButtonDisabled,
          unsupportedMsg: unsupportedMsg,
          userRole: this.getUserRole(),
          liccode: this.user.license.licCode,
          email: this.user.email
        };
      } else {
        dialogConfig.data = "upgrade";
      }
    } else if (feature == 'pdfOcr') {
      dialogConfig.data = "manualOCR upgrade";
      dialogConfig.panelClass = ['pw-s-dialog'];
      dialogConfig.backdropClass = 'pw-s-backdrop';
    } else if (feature == "bookmark") {
      dialogConfig.data = "need-login"
    }
    dialogConfig.width = '375px';
    // dialogConfig.data = {
    //   type: null,
    //   title,
    //   htmlTitle,
    //   titleTooltip,
    //   text,
    //   data: null,
    //   button_left: null,
    //   button_center: null,
    //   button_right: buttonText,
    //   button_right_disabled: orderButtonDisabled,
    //   unsupportedMsg: unsupportedMsg
    // };
    // TODO
    const dialogRef = this.dialog.open(PwAlertDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result == 'backToMenu') {
        const pwReadingMenuTrigger = document.getElementById('pwReadingMenuTrigger');
        pwReadingMenuTrigger.click();
      }
    });
  }

  public checkout(plan) {
    if (plan == 'plus') {
      // cordova use absolute url
      if (AppService.isIOS() || AppService.isAndroid()) {
        // location.href = 'https://www.naturalreaders.com/checkout/plans?plan=personal';
        this.upgradeSubscription();
      } else {
        this.showPayment('plus');
      }
    } else if (plan === 'edu') {
      if (AppService.isIOS() || AppService.isAndroid()) {
        location.href = 'https://www.naturalreaders.com/checkout-edu/'; // old
        // location.href = 'https://test.naturalreaders.com/payment/edupay/';  //edu-2022
      } else {
        window.open('../checkout-edu/', '_blank');  // old
        // window.open('../payment/edupay/', '_blank');  //edu-2022
      }
    } else if (plan === 'prem') {
      // cordova use absolute url
      if (AppService.isIOS() || AppService.isAndroid()) {
        // location.href = 'https://www.naturalreaders.com/checkout/plans?plan=personal';
        this.upgradeSubscription();
      } else {
        // location.href = '../checkout/plans?plan=personal';
        // window.open('../checkout/plans?plan=personal', '_blank');
        this.showPayment('premium');
      }
    } else {
      if (AppService.isIOS() || AppService.isAndroid()) {
        // location.href = 'https://www.naturalreaders.com/checkout/plans?plan=personal';
        this.upgradeSubscription();
      } else {
        this.showPayment('plus');
      }
    }
  }

  public showPayment(planType) {
    this.lazyLoadingService.loadPwPaymentModule().then(_ => {
      const user = this.getUser();
      const email = user.email;
      const id = user.id;
      const username = user.username;

      const dialogConfig = new MatDialogConfig();
      dialogConfig.autoFocus = false;
      dialogConfig.hasBackdrop = true;
      dialogConfig.width = '100%';
      dialogConfig.height = '100%';
      dialogConfig.id = 'upgrade-subscription-dialog';
      // dialogConfig.panelClass = 'pw-payment-panel';
      dialogConfig.panelClass = ['pw-payment-dialog', 'pw-full-handset'];
      dialogConfig.data = {
        email,
        planType
      };
      console.log('showpayment email: ', email);

      const dialogRef = this.dialog.open(PwPaymentComponent, dialogConfig);
      dialogRef.afterClosed().toPromise().then(res => {
        if (res !== undefined && res.purchased !== undefined) {
          if (res.purchased === true) {
            if (id !== '0' || username !== '') {
              this.getAndProcessLicenseAndCard(id, username);
            } else {
              this.messageService.sendMessage({ name: MessageLoginOnClick });
            }
          }
        }
      });
    }).catch(err => {
      console.log('open pwpayment err: ', err);
    });
  }

  public upgrade(type: string) {
    if (type) {
      // this.showUpgrade('prem');
      this.showUpgrade(type);
    } else {
      // this.showUpgrade(type);
      this.showUpgrade('plus');
    }
  }

}
export class SWUserService extends UserService {
  constructor(
    private http: HttpClient,
    @Inject(StorageService) storageService: StorageService,
    @Inject(MessageService) messageService: MessageService,
    @Inject(SettingsService) settingsService: SettingsService,
    @Inject(LayoutService) layout: LayoutService,
    @Inject(LazyLoadingService) lazyLoadingService: LazyLoadingService,
    // @Inject(BillService) billService: BillService,
    // private sharedUtilService: SharedUtilService,
    public dialog: MatDialog) {
    super(storageService, messageService, settingsService, layout, lazyLoadingService);
    this.user = new SWUser();
  }
  public setUser(userInfo: User): Promise<SWUser> {
    if (!userInfo) {
      this.messageService.sendMessage({ name: MessageUserUpdated, data: this.user });
      this.messageService.sendMessage({ name: MessageSWLicenseUpdated, data: this.getLicense() });
      return Promise.resolve(this.user);
    }

    const id = userInfo.id;
    const email = userInfo.email; // userid is email
    const nickname = userInfo.nickname;
    this.user = new SWUser(id, email, nickname);

    this.messageService.sendMessage({ name: MessageUserUpdated, data: this.user });
    this.getAndProcessLicense(email)
      .then(_ => {
        console.log('setUser user:', this.user);
        return Promise.resolve(this.user);
      });
  };

  public getAndProcessLicense(email = ''): Promise<any> {
    if (email.length === 0) {
      email = '';
    }

    if (email === '') {
      this.messageService.sendMessage({ name: MessageSWLicenseUpdated, data: this.getLicense() });
      return Promise.resolve(null);
    }

    let machineId = this.getMachineId();
    let machineInfoString = this.getMachineInfo();
    return this.getElectronLicense(email, machineId, machineInfoString)
      .then(license => {
        // this.user = new User(id, email, nickname, license)
        console.log('sw license: ', license);
        // this.storageService.write('pw-license', license);
        this.user.license = license;
        this.messageService.sendMessage({ name: MessageSWLicenseUpdated, data: this.getLicense() });
      });
  }

  public getElectronLicense(email: string, machineId: string, machineInfo: string): Promise<SWLicense> {
    return this.getElectronLicenseInfo(email, machineId, machineInfo)
      .then(license => {
        console.log('license', license);
        if (license) {
          if (license === 'sku_software_ultimate_mac' || license === 'sku_software_ultimate_win') {
            return Promise.resolve(SWLicense.ultimateUserLicense);
          } else if (license === 'sku_software_pro_mac' || license === 'sku_software_pro_win') {
            return Promise.resolve(SWLicense.proUserLicense);
          } else if (license === 'sku_software_personal_mac' || license === 'sku_software_personal_win') {
            return Promise.resolve(SWLicense.personalUserLicense);
          }
        }
        return Promise.resolve(SWLicense.freeUserLicense);
      });
  }

  private popMachineInvalid() {
    this.messageService.sendMessage({ name: MessagePromptOpened });
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      type: 'null',
      title: 'Device Not Authorized',
      text: 'We only allow a license to be used on at most 2 devices. Please go to the Account Information page to remove another device before using the license on this device.',
      data: null,
      button_left: "Cancel",
      button_center: null,
      button_right: 'Account Information'
    };
    // TODO
    // const dialogRef = this.dialog.open(PwAlertDialogComponent, dialogConfig);
    // dialogRef.afterClosed().subscribe(result => {
    //   if (result === 'Account Information') {
    //     this.messageService.sendMessage({ name: MessageGoToSWAccountInfo, data: { email: this.user.email } });
    //   }
    // });
  }

  private getElectronLicenseInfo(email: string, machineId: string, machineInfo: string): Promise<any> {
    const params = {
      'email': email,
      't': 's',
      'id': machineId,
      'info': machineInfo
    };
    const headers = {
      'Content-Type': 'application/json'
    };

    // let url = 'https://dwpudl295i.execute-api.us-east-1.amazonaws.com/Prod/license'; //Dev
    const url = 'https://5iusryxinl.execute-api.us-east-1.amazonaws.com/Prod/license';  // Prod
    return this.http.get(url, { params, headers }).toPromise()
      .then(response => {
        console.log('license response: ', response);
        if (response['resp'].license) {
          return Promise.resolve(response['resp'].license);
        } else {
          // TODO prompt out of license
          console.log('license error: ', response['error']);
          if (response['error'] === 'ERR_MACHINE_INVALID') {
            this.popMachineInvalid();
            this.messageService.sendMessage({ name: MessageLogout });
          }
          return Promise.resolve(null);
        }
      })
      .catch(err => {
        return Promise.reject(err);
      });
  }

  public getUserRole(): SWRole {
    if (this.user.license === SWLicense.ultimateUserLicense) {
      return SWRole.ultimate;
    } else if (this.user.license === SWLicense.proUserLicense) {
      return SWRole.pro;
    } else if (this.user.license === SWLicense.personalUserLicense) {
      return SWRole.personal;
    } else {
      return SWRole.free;
    }
  }

  public clearUser() {
    return new Promise((resolve) => {
      this.user = new SWUser();
      this.storageService.write('pw-license', this.user.license);
      resolve(null);
    })
      .then(() => {
        this.messageService.sendMessage({ name: MessageUserUpdated, data: this.user });
        this.messageService.sendMessage({ name: MessageSWLicenseUpdated, data: this.getLicense() });
      });
  }

  public getMachineId(): string {
    let machineId = '';
    if ((window as any).nodeRequire) {
      let commandpath = `${__dirname}`;
      commandpath = commandpath + '/assets/native/code.js';
      const mainProcess = (window as any).nodeRequire(commandpath);
      machineId = mainProcess.test();
    }
    console.log('machine id', machineId);
    return machineId;
  }

  public getMachineInfo(): string {
    if (AppService.isAndroid || AppService.isIOS) {
      return '';
    }
    const os = (window as any).nodeRequire('os');
    console.log(os.hostname());
    console.log(os.type());
    // let username = process.env.username || process.env.user;
    console.log(os.release());
    const info = { hostname: os.hostname(), type: os.type(), release: os.release() };
    return JSON.stringify(info);
  }

  public showUpgrade(type: string) {

  }

  public getOrderPrompt(feature) {
    switch (feature) {
      case 'ocr':
        return { msg: needOcrTextSW, text: 'Please upgrade to ULTIMATE Plan to use this feature.', plan: 'ultimate', isHtml: true };
      default:
        return { msg: 'Please upgrade to PERSONAL Plan to use this feature.', plan: 'personal' };
    }
  }

  public showOrderPrompt(feature: string) {
    console.log('showing order prompt');
    const buttonText = 'Order Now';

    // general
    const prompt = this.getOrderPrompt(feature);
    const promptMsg = prompt.msg ? prompt.msg : '';
    // const plan = prompt.plan ? prompt.plan : '';
    let htmlTitle = '';
    let title = '';
    let titleTooltip = '';
    const text = prompt.text ? prompt.text : undefined;

    if (prompt.isHtml) {
      htmlTitle = promptMsg;
    } else {
      title = promptMsg;
    }

    if (feature === 'ocr') {
      titleTooltip = whatIsOCRMsg;
    }

    this.messageService.sendMessage({ name: MessagePromptOpened });
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      type: null,
      title,
      htmlTitle,
      titleTooltip,
      text,
      data: null,
      button_left: null,
      button_center: null,
      button_right: buttonText
    };
    // TODO
    // const dialogRef = this.dialog.open(PwAlertDialogComponent, dialogConfig);
    // dialogRef.afterClosed().subscribe(result => {
    //   if (result === 'Order Now') {
    //     this.checkout(plan);
    //   }
    // });
  };

  public checkout(plan) {
    // let system = this.sharedUtilService.getSystem() === 'windows' ? 'win' : 'mac';
    // let sku = 'sku_software_' + plan + '_' + system;
    // let url = 'https://www.naturalreaders.com/checkout/plans?plan=software&code=' + sku;
    // open external window
    let url = '';
    // TODO: change to prod domain
    if (this.getUserRole() === SWRole.free) {
      url = 'https://www.naturalreaders.com/software.html#upgrade';
    } else {
      url = 'https://www.naturalreaders.com/software/account?caller=app&email=' + this.user.email;
    }

    (window as any).nodeRequire('electron').shell.openExternal(url);
  }

  public upgrade(type: string) {
    this.checkout('');
  }

  public upgradeSubscription(isUpgrade = true) {
    return null;
  }

}

export let userServiceProvider =
{
  provide: UserService,
  useFactory: userServiceFactory,
  deps: [MessageService,
    StorageService,
    SettingsService,
    LayoutService,
    LazyLoadingService,
    BillService,
    AmplifyControllerService,
    AppInstallService,
    //  SharedUtilService,
    MatDialog,
    HttpClient
  ]
};